<script setup>
import { useRouter } from "vue-router";
import { ref } from "vue";
import * as bootstrap from "bootstrap";
import McusDataService from "@/services/McusDataService";
import { readAndCompressImage } from "browser-image-resizer";
import { useStore } from "vuex";

const router = useRouter();
const store = useStore();

const fileInput = ref(null);
// チェックリストの画像を保持する配列
const checkListImageUrls = ref([]);
const checkListImageFiles = ref([]);
// 現在表示している画像のインデックス
const currentImageIndex = ref(0);

const isLoadingUploadCheckList = ref(false);

// アップロード失敗モーダルのメッセージ
const failureMessage = ref("");

// インプットVINページに戻る処理
const goBackVinInputPage = () => {
  router.push({ name: "VinInputPage" });
};

// ファイル入力をトリガーする関数
function startCaptureCheckList() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

// 画像を圧縮する関数
async function compressImage(file) {
  if (file) {
    const config = {
      quality: 0.9,
      maxWidth: 800,
      maxHeight: 800,
      autoRotate: true,
      debug: true,
    };
    try {
      return await readAndCompressImage(file, config);
    } catch (error) {
      console.error("Error while compressing image", error);
    }
  }
  return file;
}

// 画像をキャプチャしたときに呼び出される関数
async function handleCaptureCheckList(event) {
  let file = event.target.files[0];
  file = await compressImage(file);
  // 画像のBLOB URLを作成し、checkListImageUrls（ギャラリー表示用）に追加
  checkListImageUrls.value.push(URL.createObjectURL(file));
  // 画像ファイルをcheckListImageFiles（upload用）に追加
  checkListImageFiles.value.push(file);
  // 画像のインデックスを再定義
  currentImageIndex.value = checkListImageUrls.value.length - 1;
}

// 右の画像に切り替える関数
function toNextCheckList() {
  if (currentImageIndex.value < checkListImageUrls.value.length - 1) {
    currentImageIndex.value++;
  }
}

// 左の画像に切り替える関数
function toPrevCheckList() {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
  }
}

// チェックリストを削除する関数
function deleteCheckList() {
  checkListImageUrls.value.splice(currentImageIndex.value, 1);
  checkListImageFiles.value.splice(currentImageIndex.value, 1);
  // 画像が削除された後にcurrentImageIndexを更新
  currentImageIndex.value = Math.max(currentImageIndex.value - 1, 0);
}

// チェックリストアップロード後、初期画面に戻る処理
function toGoVinInputPage() {
  store.commit("setInputVin", "");
  router.push({ name: "VinInputPage" });
}

// モーダルを表示する関数
const showModal = (modalId) => {
  const modal = new bootstrap.Modal(document.getElementById(modalId));
  modal.show();
};

// アップロードエラーメッセージを生成する関数
function getUploadErrorMessage(e, imageNumber) {
  if (e.response && e.response.status >= 500) {
    return `Server error on image number ${imageNumber}.<br>Please try again later.`;
  } else if (!e.response) {
    return `Unable to send image number ${imageNumber}.<br>Please check your network and try again.`;
  } else {
    return `Failed to send image number ${imageNumber}.<br>Please retake a photo and try uploading again.`;
  }
}

// チェックリストをアップロードする関数
const uploadCheckList = async () => {
  event.preventDefault();
  isLoadingUploadCheckList.value = true;

  // 現在のアップロードセッションのためにバッチタイムスタンプを生成
  const batchTimestamp = new Date().toISOString().replace("T", " ").slice(0, 19);

  for (let i = 0; i < checkListImageFiles.value.length; i++) {
    try {
      const base64data = await convertToDataURL(checkListImageFiles.value[i]);
      console.log(`Uploading image ${i + 1}/${checkListImageFiles.value.length}`);
      await McusDataService.uploadCheckList({
        uploadData: {
          VIN: store.state.inputVin,
          uploadCheckList: base64data,
          imageIndex: i + 1,
          batchTimestamp: batchTimestamp,
        },
        log: store.state.logData,
      });
    } catch (e) {
      console.log(`NG: ${i + 1}枚目が送れませんでした`);
      failureMessage.value = getUploadErrorMessage(e, i + 1);
      showModal("uploadfailure");
      isLoadingUploadCheckList.value = false;
      return;
    }
  }

  // すべての画像が正常に送信された場合
  showModal("uploadsucces");
  isLoadingUploadCheckList.value = false;
};

// 画像ファイルをbase64 URLに変換する関数
function convertToDataURL(image) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      resolve(event.target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(image);
  });
}
</script>

<template>
  <!-- ローディング状態を表示する要素 -->
  <div v-if="isLoadingUploadCheckList" class="loading">
    <div class="spinner-border text-light" role="status">
      <span class="visually-hidden"></span>
    </div>
  </div>
  <!-- アップロードの確認モーダル -->
  <div
    class="modal fade"
    id="uploadConfirmModal"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="static"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header"></div>
        <div class="modal-body">
          <p class="text-dark fw-bold">Do you want to upload?</p>
        </div>
        <div class="modal-footer justify-content-center">
          <button
            type="button"
            class="btn btn btn-outline-dark custom-border-btn"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            data-bs-dismiss="modal"
            @click="uploadCheckList"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- チェックリスト削除確認モーダル -->
  <div
    class="modal fade"
    id="deleteConfirmModal"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="static"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header"></div>
        <div class="modal-body">
          <p class="text-dark fw-bold">Do you want to Delete?</p>
        </div>
        <div class="modal-footer justify-content-center">
          <button
            type="button"
            class="btn btn btn-outline-dark custom-border-btn"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-danger"
            data-bs-dismiss="modal"
            @click="deleteCheckList"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- アップロード成功モーダル -->
  <div
    class="modal fade"
    id="uploadsucces"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="static"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header"></div>
        <div class="modal-body">
          <p class="text fw-bold" style="color: blue">Upload Success</p>
        </div>
        <div class="modal-footer justify-content-center">
          <button
            type="button"
            class="btn btn-primary"
            data-bs-dismiss="modal"
            @click="toGoVinInputPage"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- アップロード失敗モーダル -->
  <div
    class="modal fade"
    id="uploadfailure"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="static"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header"></div>
        <div class="modal-body">
          <p v-html="failureMessage" class="text-danger fw-bold"></p>
        </div>
        <div class="modal-footer justify-content-center">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="container">
    <div id="header" class="pt-3 pb-3 text-start">
      Step2: Capture Setup Checklists
    </div>
    <div class="info-section">
      <div class="info-row">
        <label class="fs-8">Distributor:</label>
        <p>{{ store.getters.companyName }}</p>
      </div>
      <div class="info-row">
        <label class="fs-8">VIN:</label>
        <p>{{ store.state.inputVin }}</p>
      </div>
    </div>
    <div class="camera-section">
      <!-- 入力を受付するインプット要素 -->
      <input
        type="file"
        accept="image/*"
        capture="environment"
        @change="handleCaptureCheckList"
        style="display: none"
        ref="fileInput"
      />
      <!-- 画像ギャラリー -->
      <div v-if="checkListImageUrls.length > 0">
        <!-- 削除確認モーダルを表示させるボタン -->
        <button
          class="btn btn-danger delete-button"
          @click="showModal('deleteConfirmModal')"
          style="background-color: white; color: red; border: 1px solid red"
        >
          <i class="bi bi-trash-fill fs-3"></i>
        </button>
        <img
          :src="checkListImageUrls[currentImageIndex]"
          alt="Captured"
          style="max-width: 100%; height: auto; object-fit: contain"
        />
      </div>
    </div>
    <!-- 画像の選択をできる矢印ボタンとチェックリストの画像のインデックスを配置 -->
    <div v-if="checkListImageUrls.length > 0" class="checkList-gallery-controls">
      <button class="btn btn-light" @click="toPrevCheckList">
        <i class="bi bi-arrow-left"></i>
      </button>
      <div class="image-count">
        {{ currentImageIndex + 1 }} / {{ checkListImageUrls.length }}
      </div>
      <button class="btn btn-light" @click="toNextCheckList">
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>
    <!-- Back, Add, Upload ボタン表示 -->
    <div class="d-flex justify-content-center align-items-center mt-4">
      <button
        v-if="checkListImageUrls.length == 0"
        class="btn btn-danger mx-3"
        @click="goBackVinInputPage"
      >
        <i class="bi bi-arrow-left"></i> Back
      </button>
      <button
        class="btn btn-primary d-flex align-items-center mx-3"
        style="border: 1px solid #007bff; background-color: white; color: #007bff"
        @click="startCaptureCheckList"
      >
        Add
        <i class="bi bi-camera ms-2"></i>
      </button>
      <button
        v-if="checkListImageUrls.length > 0"
        class="btn btn-primary mx-3"
        @click="showModal('uploadConfirmModal')"
      >
        Upload
        <i class="bi bi-upload ms-2"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
#header {
  font-size: 120%; /* 文字サイズを大きく設定 */
  margin-left: 10px;
  font-weight: bold;
  margin-bottom: 5px;
}

label {
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 1em; /* 文字サイズを大きく設定 */
  margin-left: 5px;
  margin-right: 5px;
}

button {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 100px;
  height: 40px;
  font-weight: bold;
}

button.d-flex {
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.info-section {
  margin-top: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.info-row {
  display: flex;
  width: 100%;
  justify-content: center;
}

.camera-section {
  margin-top: 5px;
  width: 100%;
  height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  border: 2px dashed #ccc;
  background-color: #f2f2f2;
  position: relative;
}

.container {
  display: flex;
  flex-direction: column;
  height: 90vh;
}

i.bi-camera {
  font-size: 1.5em;
}

.checkList-gallery-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.delete-button {
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 1;
  min-width: 40px; /* 幅を小さく調整 */
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
}
/* ローディング状態を表示するスタイルを追加 */
.loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
/* キャンセルボタンの枠線表示 */
.custom-border-btn {
  border: 1px solid rgb(180, 180, 180); /* 枠線の色と太さを指定 */
}
/* モーダルの薄い横線の削除 */
.modal-header,
.modal-footer {
  border-bottom: none; /* 下線を除去 */
  border-top: none; /* 上線を除去 */
}
</style>
